import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { modelTier } from './ModelTier';
import { isDifficulty, suitsTier } from './BehaviorModelFitViewModel';

/** The classified per-interaction model-fit score (1–100), or null when unscored. */
export function recordFitScore(record: InteractionRecord): number | null {
    const num = record.dimensions['model_fit']?.valueNum;
    return typeof num === 'number' && num > 0 ? Math.round(num) : null;
}

export interface RecordWaste {
    /** Allocated cost of this prompt when it went to a tier-inappropriate model, else 0. */
    cost: number;
    /** Total tokens of this prompt when it went to a tier-inappropriate model, else 0. */
    tokens: number;
    /** Whether this prompt's model tier didn't suit its task difficulty. */
    misfit: boolean;
}

/**
 * A prompt's waste — the cost and tokens spent when its model tier didn't suit
 * the task difficulty (premium on easy, or standard on hard). Zero when the
 * prompt is a good fit or has no classified difficulty.
 */
/** Per-prompt routing verdict: is this model right-sized for the task, or should it move up/down a tier? */
export type FitSuggestionKind = 'good' | 'stronger' | 'lighter' | 'unknown';

/**
 * Suggests whether this prompt should have used a stronger or lighter model:
 * `lighter` when a premium model handled easy work (over-provisioned), `stronger`
 * when a standard model handled hard work (under-provisioned), `good` when the
 * tier suited the difficulty, and `unknown` when there's no classified difficulty.
 */
export function recordSuggestion(record: InteractionRecord): FitSuggestionKind {
    const difficulty = record.dimensions['difficulty']?.value;
    if (!isDifficulty(difficulty)) return 'unknown';
    const tier = modelTier(record.model);
    if (suitsTier(tier, difficulty)) return 'good';
    return tier === 'premium' ? 'lighter' : 'stronger';
}

export function recordWaste(record: InteractionRecord): RecordWaste {
    const difficulty = record.dimensions['difficulty']?.value;
    if (!isDifficulty(difficulty)) return { cost: 0, tokens: 0, misfit: false };
    const misfit = !suitsTier(modelTier(record.model), difficulty);
    const tokens = record.inputTokens + record.outputTokens;
    return { cost: misfit ? record.allocatedCostUsd : 0, tokens: misfit ? tokens : 0, misfit };
}
